import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../store/store';
import { EventData } from '../../util/types';
import { getClubEvents } from '../../services/events';
import { getAuth } from "firebase/auth";


// Define the initial state
interface EventsState {
    events: EventData[];
    lastFetchCount: number;
    loading: boolean;
    error: string | null | undefined;
    currentClubId: string | null;
    currentRequestId: string | null;
}

const initialState: EventsState = {
    events: [],
    lastFetchCount: 0,
    loading: false,
    error: null,
    currentClubId: null,
    currentRequestId: null,
};

// Create an async thunk to fetch events
export const fetchEvents = createAsyncThunk('events/fetchEvents', async ({ clubId, forceRefresh }: { clubId: string, forceRefresh?: boolean }) => {
    try {
        const auth = getAuth();
        const token = await auth.currentUser?.getIdToken();
        if (!token) {
            throw new Error("Failed to retrieve Token")
        }
        // Fetch events from your API
        const res = await getClubEvents(clubId, token, forceRefresh);
        // Return the events
        return res;
    } catch (error) {
        // Handle errors
        throw new Error('Failed to fetch events');
    }
});

// Create a slice for events
const eventsSlice = createSlice({
    name: 'events',
    initialState,
    reducers: {
        removeEventById(state, action) {
            state.events = state.events.filter((event) => event.eventId !== action.payload);
            state.lastFetchCount = state.events.length;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchEvents.pending, (state, action) => {
                state.loading = true;
                state.error = null;
                state.currentClubId = action.meta.arg.clubId;
                state.currentRequestId = action.meta.requestId;
                state.events = [];
                state.lastFetchCount = 0;
            })
            .addCase(fetchEvents.fulfilled, (state, action) => {
                if (
                    state.currentRequestId !== action.meta.requestId ||
                    state.currentClubId !== action.meta.arg.clubId
                ) {
                    return;
                }
                state.loading = false;
                state.events = action.payload.events ?? [];
                state.lastFetchCount = action.payload.len ?? action.payload.events?.length ?? 0;
                state.currentRequestId = null;
            })
            .addCase(fetchEvents.rejected, (state, action) => {
                if (
                    state.currentRequestId !== action.meta.requestId ||
                    state.currentClubId !== action.meta.arg.clubId
                ) {
                    return;
                }
                state.loading = false;
                state.error = action.error.message;
                state.currentRequestId = null;
            });
    },
});

// Export the reducer
export default eventsSlice.reducer;
export const { removeEventById } = eventsSlice.actions;

// Selectors
export const selectEvents = (state: RootState) => state.events.events;
export const selectEventsLoading = (state: RootState) => state.events.loading;
export const selectEventsError = (state: RootState) => state.events.error;
